import type { NestRefusal, Task, TaskPlacement } from './models'
import { roundHistoryOf, withoutSucceededRounds } from './taskFilters'
import { ancestorsOf, depthOf, descendantsOf, MAX_SUBTASK_DEPTH, rootOf, subtreeHeight } from './taskTree'

/*
 * **Einhängen und Herauslösen** (#204, CONTEXT.md). Die Regeln stehen hier einmal, als
 * reine Rechnung; Dexie und die Speicher-Quelle holen die Zeilen und schreiben das Ergebnis.
 */

export function placementOf(task: Task): TaskPlacement {
  const { id, parentTaskId, order, projectId, sectionId, lifeSpace, createdAt } = task
  return { id, parentTaskId, order, projectId, sectionId, lifeSpace, createdAt }
}

export type NestPlan = { refused: NestRefusal } | { before: TaskPlacement[]; after: TaskPlacement[] }

const live = (tasks: readonly Task[]) => tasks.filter(t => !t.deletedAt)
const childrenOf = (tasks: readonly Task[], id: string) => tasks.filter(t => t.parentTaskId === id).sort((a, b) => a.order - b.order)

/**
 * Die angefassten Zeilen, die selbst ziehen: ohne die, deren Vorfahr auch angefasst ist — die
 * reisen in dessen Ast mit (#181). Reihenfolge wie `ids`, doppelte und fehlende fallen weg.
 */
function headsOf(tasks: readonly Task[], ids: readonly string[]): Task[] {
  const picked = new Set(ids)
  const byId = new Map(tasks.map(t => [t.id, t]))
  return [...picked]
    .map(id => byId.get(id))
    .filter((t): t is Task => t !== undefined && !ancestorsOf(tasks, t.id).some(a => picked.has(a.id)))
}

/** Die Zeilen in der Reihenfolge der Checkliste (Vorordnung unter ihrer Aufgabe). */
function inChecklistOrder(tasks: readonly Task[], rows: readonly Task[]): Task[] {
  const position = new Map<string, number>()
  for (const row of rows) {
    const root = rootOf(tasks, row.id)
    if (root && !position.has(row.id)) descendantsOf(tasks, root.id).forEach((t, i) => position.set(t.id, i))
  }
  return [...rows].sort((a, b) => (position.get(a.id) ?? 0) - (position.get(b.id) ?? 0))
}

/**
 * Die alten Runden einer Unteraufgabe samt ihren Ästen, aus dem Baum ihrer Aufgabe — sie reisen mit,
 * wenn die Zeile diesen Baum verlässt (Umhängen, Herauslösen). Eine Aufgabe hat keine: Ihre alten
 * Runden sind eigene Aufgaben in der Liste.
 */
function historyWithBranches(rows: readonly Task[], head: Task): { rounds: Task[]; branches: Task[] } {
  if (head.parentTaskId == null) return { rounds: [], branches: [] }
  const root = rootOf(rows, head.id)
  const rounds = root ? roundHistoryOf(descendantsOf(rows, root.id), head) : []
  return { rounds, branches: rounds.flatMap(round => descendantsOf(rows, round.id)) }
}

/** `count` Werte für `order` direkt vor `order`, über `lower` (oder `order − 1`). */
function ordersBefore(order: number, lower: number | undefined, count: number): (i: number) => number {
  const floor = lower ?? order - 1
  return i => floor + ((order - floor) * (i + 1)) / (count + 1)
}

/** Wiederholt sich etwas im Ast von `row` — sie selbst oder eine Zeile darunter? */
const branchRecurs = (tasks: readonly Task[], row: Task) => [row, ...descendantsOf(tasks, row.id)].some(t => t.recurrence != null)

/**
 * Hängt `ids` (in dieser Reihenfolge) in die Checkliste von `parentId`: hinter `afterId`,
 * `null` ganz vorn, ohne Angabe ans Ende. Ein Schreibvorgang — die Zeile springt nicht erst
 * ans Ende und dann an ihren Platz (gemeldet 27.09.2026).
 *
 * - Das Ziel ist eine Aufgabe, keine Unteraufgabe (#181, Grill 9): Die Zeile landet auf Ebene 1,
 *   tiefer geht es mit {@link planIndent}.
 * - **Der Ast zieht mit** (#181): Eigene Unteraufgaben bleiben ihre Kinder, beliebig tief. Er passt
 *   nur, wenn er unter der Aufgabe nicht tiefer als {@link MAX_SUBTASK_DEPTH} Ebenen reicht.
 * - Kein Ziel ist eine erledigte Aufgabe oder eine aus `ids`.
 * - Wiederkehrend in wiederkehrend gibt es nicht (#205): Unter einer wiederkehrenden Aufgabe gilt
 *   ihr Rhythmus — geprüft im ganzen Ast.
 * - Ort und Lebensbereich kommen vom Elternteil, für den ganzen Ast; alles andere (Datum,
 *   Belohnung, Ruhe …) bleibt.
 *
 * `tasks` braucht das Ziel, seine Kinder und die gezogenen Zeilen samt Ast.
 */
export function planNest(tasks: readonly Task[], ids: readonly string[], parentId: string, afterId?: string | null): NestPlan {
  const rows = live(tasks)
  const parent = rows.find(t => t.id === parentId)
  if (!parent || parent.parentTaskId != null || parent.status === 'completed' || ids.includes(parentId)) {
    return { refused: 'invalid' }
  }

  const heads = headsOf(rows, ids).filter(t => t.parentTaskId !== parentId)
  if (parent.recurrence != null && heads.some(t => branchRecurs(rows, t))) return { refused: 'recurring' }
  if (heads.some(t => 1 + subtreeHeight(rows, t.id) > MAX_SUBTASK_DEPTH)) return { refused: 'depth' }

  const headIds = new Set(heads.map(t => t.id))
  const staying = childrenOf(rows, parentId).filter(c => !headIds.has(c.id))
  const orderAt = slotOrders(staying, afterId, heads.length)
  const location = { projectId: parent.projectId, sectionId: parent.sectionId, lifeSpace: parent.lifeSpace }
  const before: TaskPlacement[] = []
  const after: TaskPlacement[] = []
  const taken = staying.map(c => c.order)
  heads.forEach((head, i) => {
    const order = orderAt(i)
    // Die alten Runden (ausgeblendet) stehen direkt vor ihrer Zeile, samt ihren Ästen (#181).
    const history = head.parentTaskId === parentId ? { rounds: [], branches: [] } : historyWithBranches(rows, head)
    const lower = Math.max(-Infinity, ...taken.filter(o => o < order))
    const roundOrder = ordersBefore(order, Number.isFinite(lower) ? lower : undefined, history.rounds.length)
    taken.push(order)
    for (const [k, round] of history.rounds.entries()) {
      before.push(placementOf(round))
      after.push({ ...placementOf(round), ...location, parentTaskId: parentId, order: roundOrder(k) })
    }
    for (const t of [head, ...descendantsOf(rows, head.id), ...history.branches]) {
      before.push(placementOf(t))
      after.push(t === head
        ? { ...placementOf(t), ...location, parentTaskId: parentId, order }
        : { ...placementOf(t), ...location })
    }
  })
  return { before, after }
}

/**
 * **Einrücken** (⌥→, #181): Jede angefasste Zeile hängt unter die Zeile darüber auf derselben
 * Ebene, als letztes Kind; ihr Ast zieht mit. Mehrere untereinander gehen zusammen unter die Zeile
 * über der ersten, in ihrer Reihenfolge. Alle oder keine:
 *
 * - `invalid`: keine Zeile darüber (erste ihrer Ebene) oder keine Unteraufgabe.
 * - `completed`: Die Zeile darüber ist erledigt — darunter stünde eine offene Zeile unter einer
 *   erledigten, wie beim Einhängen in eine erledigte Aufgabe.
 * - `depth`: Der Ast reichte tiefer als {@link MAX_SUBTASK_DEPTH}.
 * - `recurring`: Im Ast wiederholt sich etwas, und die Zeile darüber oder ein Vorfahr auch.
 *
 * Ausgeblendete erledigte Runden (`withoutSucceededRounds`) sind nie „die Zeile darüber“.
 * `tasks`: der Baum der Aufgabe (sie und alle Nachkommen).
 */
export function planIndent(tasks: readonly Task[], ids: readonly string[]): NestPlan {
  const rows = live(tasks)
  const heads = inChecklistOrder(rows, headsOf(rows, ids).filter(t => t.parentTaskId != null))
  if (heads.length === 0) return { refused: 'invalid' }
  const moving = new Set(heads.map(t => t.id))
  const nextOrder = new Map<string, number>()
  const after: TaskPlacement[] = []
  for (const head of heads) {
    // Der Nachfolger einer Runde kann auf jeder Ebene stehen — gesucht wird im ganzen Baum der Aufgabe.
    const root = rootOf(rows, head.id)
    const siblings = withoutSucceededRounds(childrenOf(rows, head.parentTaskId!), root ? descendantsOf(rows, root.id) : rows)
    const index = siblings.findIndex(t => t.id === head.id)
    const target = index === -1 ? undefined : siblings.slice(0, index).reverse().find(t => !moving.has(t.id))
    if (!target) return { refused: 'invalid' }
    if (target.status === 'completed') return { refused: 'completed' }
    if (depthOf(rows, target.id) + 1 + subtreeHeight(rows, head.id) > MAX_SUBTASK_DEPTH) return { refused: 'depth' }
    if (branchRecurs(rows, head) && [target, ...ancestorsOf(rows, target.id)].some(t => t.recurrence != null)) {
      return { refused: 'recurring' }
    }
    const order = nextOrder.get(target.id) ?? childrenOf(rows, target.id).reduce((max, t) => Math.max(max, t.order), -1) + 1
    nextOrder.set(target.id, order + 1)
    after.push({ ...placementOf(head), parentTaskId: target.id, order })
  }
  return { before: heads.map(placementOf), after }
}

/**
 * **Ausrücken** (⌥←, #181): Jede angefasste Zeile rückt eine Ebene hinauf, direkt hinter ihren
 * bisherigen Eltern-Teil; ihr Ast zieht mit, Geschwister bleiben. Zeilen auf Ebene 1 bleiben
 * still stehen (Grill, Entscheidung 5) — herausgelöst wird nur mit ⇧⌥↑/↓.
 * `tasks`: der Baum der Aufgabe (sie und alle Nachkommen).
 */
export function planOutdent(tasks: readonly Task[], ids: readonly string[]): NestPlan {
  const rows = live(tasks)
  const heads = inChecklistOrder(rows, headsOf(rows, ids).filter(t => depthOf(rows, t.id) >= 2))
  const moving = new Set(heads.map(t => t.id))
  const byParent = new Map<string, Task[]>()
  for (const head of heads) byParent.set(head.parentTaskId!, [...(byParent.get(head.parentTaskId!) ?? []), head])
  const after: TaskPlacement[] = []
  for (const [parentId, group] of byParent) {
    const parent = rows.find(t => t.id === parentId)!
    const orderAt = slotOrders(childrenOf(rows, parent.parentTaskId!).filter(t => !moving.has(t.id)), parent.id, group.length)
    group.forEach((head, i) => after.push({ ...placementOf(head), parentTaskId: parent.parentTaskId, order: orderAt(i) }))
  }
  return { before: heads.map(placementOf), after }
}

/** `count` Werte für `order` hinter `afterId` unter den Geschwistern; ohne Angabe oder ohne Platz am Ende. */
function slotOrders(siblings: readonly Task[], afterId: string | null | undefined, count: number): (i: number) => number {
  const end = siblings.reduce((max, c) => Math.max(max, c.order), -1) + 1
  if (afterId === undefined) return i => end + i
  const index = afterId === null ? -1 : siblings.findIndex(c => c.id === afterId)
  if (afterId !== null && index === -1) return i => end + i
  const next = siblings[index + 1]
  if (!next) return i => end + i
  const lower = index === -1 ? next.order - 1 : siblings[index]!.order
  const step = (next.order - lower) / (count + 1)
  // Kein Platz mehr zwischen den beiden (gleiche Werte): lieber ans Ende als übereinander.
  if (!(step > 0)) return i => end + i
  return i => lower + step * (i + 1)
}

/**
 * Wohin herausgelöst wird: an eine Stelle oder über/unter die bisherige Aufgabe. `createdAt` an der
 * Stelle: das Anlegedatum der Nachbarzeile in **Kurz notiert**, damit sie im selben Abschnitt landet.
 */
export type UnnestAt = { sectionId: string | null; order: number; createdAt?: Date } | 'above' | 'below'

/**
 * Macht eine Unteraufgabe wieder zur Aufgabe — an einer Stelle der Liste (Ziehen), oder
 * **direkt über bzw. unter ihrer bisherigen Aufgabe** (⇧⌥↑ / ⇧⌥↓, Knopf in den Details).
 * Projekt und Lebensbereich bleiben die ihres Elternteils. Ihr Ast zieht mit (#181) und folgt
 * ihrem Abschnitt. `null`: keine Unteraufgabe.
 *
 * @param root für `above`/`below`: die Aufgabe ganz oben in ihrer Kette (#181) — neben ihr landet sie.
 * @param listed für `above`/`below`: die Aufgaben derselben Liste (oberste Ebene), in der
 *               `root` steht — daraus kommen seine Nachbarn.
 * @param sectionOrder `order` des Abschnittskopfs über `root`: Darüber darf nichts
 *               landen (Abschnitte und Aufgaben teilen sich die Zahlen).
 * @param family der Baum ihrer Aufgabe (die Aufgabe ganz oben und alle Nachkommen) — daraus kommen
 *               ihr Ast und ihre alten Runden, die mitziehen.
 */
export function planUnnest(
  task: Task | undefined,
  at: UnnestAt = 'below',
  root?: Task,
  listed: readonly Task[] = [],
  sectionOrder?: number,
  family: readonly Task[] = [],
): { before: TaskPlacement[]; after: TaskPlacement[] } | null {
  const head = planUnnestRow(task, at, root, listed, sectionOrder)
  if (!head || !task) return null
  const rows = live(family)
  const sectionId = head.after.sectionId
  // Die alten Runden werden erledigte Runden der neuen Aufgabe (#181) — direkt vor ihr in der Liste.
  const history = historyWithBranches(rows.some(t => t.id === task.id) ? rows : [task, ...rows], task)
  const lower = Math.max(-Infinity, ...listed
    .filter(t => !t.deletedAt && t.parentTaskId == null && (t.sectionId ?? null) === (sectionId ?? null) && t.order < head.after.order)
    .map(t => t.order))
  const roundOrder = ordersBefore(head.after.order, Number.isFinite(lower) ? lower : undefined, history.rounds.length)
  const follows = [...descendantsOf(rows, task.id), ...history.branches].filter(t => (t.sectionId ?? null) !== sectionId)
  return {
    before: [head.before, ...history.rounds.map(placementOf), ...follows.map(placementOf)],
    after: [
      head.after,
      ...history.rounds.map((round, k) => ({ ...placementOf(round), parentTaskId: null, sectionId, order: roundOrder(k) })),
      ...follows.map(t => ({ ...placementOf(t), sectionId })),
    ],
  }
}

function planUnnestRow(
  task: Task | undefined,
  at: UnnestAt,
  root: Task | undefined,
  listed: readonly Task[],
  sectionOrder: number | undefined,
): { before: TaskPlacement; after: TaskPlacement } | null {
  if (!task || task.deletedAt || task.parentTaskId == null) return null
  const before = placementOf(task)
  if (typeof at === 'object') {
    return { before, after: { ...before, parentTaskId: null, sectionId: at.sectionId, order: at.order, createdAt: at.createdAt ?? before.createdAt } }
  }
  if (!root) return null
  const neighbours = listed.filter(t => !t.deletedAt && t.parentTaskId == null && t.id !== root.id
    && (t.sectionId ?? null) === (root.sectionId ?? null))
  let order: number
  if (at === 'below') {
    const next = neighbours.filter(t => t.order > root.order).reduce<Task | undefined>((min, t) => (!min || t.order < min.order ? t : min), undefined)
    order = next ? (root.order + next.order) / 2 : root.order + 1
  } else {
    const prev = neighbours.filter(t => t.order < root.order).reduce<Task | undefined>((max, t) => (!max || t.order > max.order ? t : max), undefined)
    const floor = prev?.order ?? sectionOrder
    order = floor !== undefined ? (floor + root.order) / 2 : root.order - 1
  }
  // Ohne Projekt steht sie in Kurz notiert: Mit ihrem eigenen Anlegedatum landete sie womöglich in einem
  // anderen Abschnitt als ihre Aufgabe (gemeldet 28.09.2026).
  const createdAt = root.projectId == null ? root.createdAt : before.createdAt
  return { before, after: { ...before, parentTaskId: null, sectionId: root.sectionId ?? null, order, createdAt } }
}

/** Die Liste, in der eine Aufgabe steht: ihr Projekt, ohne Projekt ihr Lebensbereich (Kurz notiert). */
export function sameList(a: Task, b: Task): boolean {
  return a.projectId ? a.projectId === b.projectId : !b.projectId && a.lifeSpace === b.lifeSpace
}
